"use client";

import { useEffect, useRef, useState } from "react";

import { getGeoCountryFromCookie } from "@utilities/getGeoCountry";

type GeoOverrideResult<T> = {
  /** The resolved override value, or null while loading. */
  resolved: T | null;
  /** null = loading, false = no override needed, true = override needed. */
  needsOverride: boolean | null;
};

/**
 * Client-side hook for geo-targeted content resolution.
 *
 * Reads the `geo_country` cookie, checks if the user's country is in the
 * override list, and if so calls `fetchOverride` to get the override data.
 * Falls back to `fallback` if the fetch fails.
 *
 * Note: `fallback` is captured by ref — callers don't need to memoize it.
 */
export function useGeoOverride<T>({
  overrideCountries,
  fetchOverride,
  fallback,
}: {
  overrideCountries: string[];
  fetchOverride: () => Promise<T | null>;
  fallback: T;
}): GeoOverrideResult<T> {
  const [resolved, setResolved] = useState<T | null>(null);
  const [needsOverride, setNeedsOverride] = useState<boolean | null>(null);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  useEffect(() => {
    const country = getGeoCountryFromCookie();
    const isOverrideCountry = !!country && overrideCountries.includes(country);

    if (!isOverrideCountry) {
      setNeedsOverride(false);
      return;
    }

    setNeedsOverride(true);

    let cancelled = false;
    fetchOverride()
      .then(data => {
        if (!cancelled) {
          setResolved(data ?? fallbackRef.current);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolved(fallbackRef.current);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [overrideCountries, fetchOverride]);

  return { resolved, needsOverride };
}
